import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '@/contexts/LanguageContext';
import { getNotificationPhone, PHONE_KEY, requestPhoneCode, verifyPhoneCode, removeNotificationPhone } from '@/api/preferences';

export default function NotificationPhone() {
  const { t } = useLanguage();
  const client = useQueryClient();
  const saved = useQuery({ queryKey: PHONE_KEY, queryFn: getNotificationPhone });
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [mocked, setMocked] = useState(false);
  const changed = () => {
    setPassword(''); setCode(''); setChallenge(null); setPhone('');
    void client.invalidateQueries({ queryKey: PHONE_KEY });
    void client.invalidateQueries({ queryKey: ['notification-preferences'] });
  };
  const request = useMutation({ mutationFn: requestPhoneCode, onSuccess: (result) => {
    setChallenge(result.challenge_id); setPassword(''); setCode(''); setMocked(result.delivery_status === 'MOCKED');
  } });
  const verify = useMutation({ mutationFn: verifyPhoneCode, onSuccess: changed });
  const remove = useMutation({ mutationFn: removeNotificationPhone, onSuccess: changed });
  const pending = request.isPending || verify.isPending || remove.isPending;
  function submit(event: FormEvent) {
    event.preventDefault();
    if (challenge) verify.mutate({ challenge_id: challenge, code });
    else request.mutate({ phone, current_password: password });
  }
  return <div className="mt-4 rounded-xl border border-gray-200 p-4" aria-label={t('m3.phone.title')}>
    <h3 className="font-semibold">{t('m3.phone.title')}</h3>
    <p className="mt-1 text-sm text-gray-600">{t('m3.phone.description')}</p>
    {saved.isPending && <p role="status">{t('common.loading')}</p>}
    {saved.isError && <p role="alert">{t('m3.phone.loadError')}</p>}
    {saved.data && <>
      <p className="mt-2 text-sm" role="status">{saved.data.verified ? t('m3.phone.verified', { phone: saved.data.phone }) : t('m3.phone.unverified')}</p>
      <form onSubmit={submit} className="mt-3 space-y-3">
        {!challenge ? <>
          <label className="block text-sm">{t('m3.phone.number')}<input type="tel" autoComplete="tel" required maxLength={32} value={phone} onChange={event => setPhone(event.target.value)} disabled={pending} className="mt-1 block w-full rounded-lg border p-2" /></label>
          <label className="block text-sm">{t('m3.phone.password')}<input type="password" autoComplete="current-password" required value={password} onChange={event => setPassword(event.target.value)} disabled={pending} className="mt-1 block w-full rounded-lg border p-2" /></label>
        </> : <>
          <p className="text-sm" role="status">{t(mocked ? 'm3.phone.mocked' : 'm3.phone.codeSent')}</p>
          <label className="block text-sm">{t('m3.phone.code')}<input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={event => setCode(event.target.value)} disabled={pending} className="mt-1 block w-full rounded-lg border p-2" /></label>
        </>}
        <div className="flex flex-wrap gap-3">
          <button type="submit" disabled={pending || saved.isError} className="rounded-lg bg-blue-900 px-4 py-2 text-sm text-white disabled:opacity-50">{t(pending ? 'm3.saving' : challenge ? 'm3.phone.verify' : 'm3.phone.request')}</button>
          {challenge && <button type="button" onClick={() => { setChallenge(null); setCode(''); }} disabled={pending} className="text-sm underline">{t('m3.phone.restart')}</button>}
          {saved.data.verified && !challenge && <button type="button" disabled={!password || pending} onClick={() => remove.mutate(password)} className="text-sm text-red-800 underline">{t('m3.phone.remove')}</button>}
        </div>
      </form>
      {(request.isError || verify.isError || remove.isError) && <p role="alert" className="mt-2 text-sm text-red-700">{t('m3.phone.error')}</p>}
      {verify.isSuccess && <p role="status" className="mt-2 text-sm text-green-800">{t('m3.phone.saved')}</p>}
    </>}
  </div>;
}
